import _ from "lodash";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Modal, Spin, message } from "antd";
import { LoadingOutlined } from "@ant-design/icons";

import FiabiliteComponent from "components/dashboard/fiabilite";
import ModalDynamicInformation from "components/dashboard/modal-dynamic-information";
import API from "services/api";
import baseApi, { HEADER_KEY } from "services/api/base.api";
import QUERY_KEYS from "services/api/queryKeys";
import { getUrlParam } from "utils/iframeKey";

import "styles/dashboard.scss";

const Fiabilite = FiabiliteComponent as any;
const ModalDynamicInfo = ModalDynamicInformation as any;

function IframeFiabilite() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [messageApi, contextHolder] = message.useMessage();

  const [isTokenSet, setIsTokenSet] = useState(false);
  const [svgFiabilite, setSvgFiabilite] = useState("");
  const [dataModalFiabilite, setDataModalFiabilite] = useState<any>(null);
  const [isShowModalFiabilite, setIsShowModalFiabilite] = useState(false);

  useEffect(() => {
    const tokenFromUrl = getUrlParam("token") || getUrlParam("key");
    if (tokenFromUrl) {
      baseApi.setToken(tokenFromUrl);
    }

    const intentIdFromUrl = searchParams.get("intent_id");
    if (intentIdFromUrl) {
      baseApi.setDefaultHeader(HEADER_KEY.X_INTENT_ID, intentIdFromUrl);
    }

    if (tokenFromUrl) {
      setIsTokenSet(true);
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "setToken" && event.data?.token) {
        baseApi.setToken(event.data.token);
        if (event.data.intentId) {
          baseApi.setDefaultHeader(HEADER_KEY.X_INTENT_ID, event.data.intentId);
        }
        setIsTokenSet(true);
      }

      if (typeof event.data === "string") {
        const token = event.data.startsWith("Bearer ")
          ? event.data.slice(7)
          : event.data;

        if (token && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(token)) {
          baseApi.setToken(token);
          setIsTokenSet(true);
        }
      }
    };

    window.addEventListener("message", handleMessage);

    const timeout = setTimeout(() => {
      setIsTokenSet(true);
    }, 100);

    return () => {
      window.removeEventListener("message", handleMessage);
      clearTimeout(timeout);
    };
  }, [searchParams]);

  useEffect(() => {
    const previousBodyOverflow = document.body.style.getPropertyValue("overflow");
    const previousBodyOverflowPriority = document.body.style.getPropertyPriority("overflow");
    const previousBodyOverflowX = document.body.style.getPropertyValue("overflow-x");
    const previousBodyOverflowXPriority = document.body.style.getPropertyPriority("overflow-x");
    const previousBodyOverflowY = document.body.style.getPropertyValue("overflow-y");
    const previousBodyOverflowYPriority = document.body.style.getPropertyPriority("overflow-y");
    const previousHtmlOverflow = document.documentElement.style.getPropertyValue("overflow");
    const previousHtmlOverflowPriority = document.documentElement.style.getPropertyPriority("overflow");
    const previousHtmlOverflowX = document.documentElement.style.getPropertyValue("overflow-x");
    const previousHtmlOverflowXPriority = document.documentElement.style.getPropertyPriority("overflow-x");
    const previousHtmlOverflowY = document.documentElement.style.getPropertyValue("overflow-y");
    const previousHtmlOverflowYPriority = document.documentElement.style.getPropertyPriority("overflow-y");
    const cookieHubSelector = [
      "#cookiehub-widget",
      ".cookiehub-widget",
      "[id*=\"cookiehub\"]",
      "[class*=\"cookiehub\"]",
      "[data-region][class*=\"ch2-region\"]",
      ".ch2",
      ".ch2-container",
      ".ch2-dialog",
      ".ch2-icon",
      "style[data-ch2inline=\"true\"]",
    ].join(", ");

    const restoreProperty = (
      target: CSSStyleDeclaration,
      property: string,
      value: string,
      priority: string
    ) => {
      if (value) {
        target.setProperty(property, value, priority);
        return;
      }
      target.removeProperty(property);
    };

    const enforceIframeStyles = () => {
      document.body.style.setProperty("overflow", "auto", "important");
      document.body.style.setProperty("overflow-x", "auto", "important");
      document.body.style.setProperty("overflow-y", "auto", "important");
      document.documentElement.style.setProperty("overflow", "auto", "important");
      document.documentElement.style.setProperty("overflow-x", "auto", "important");
      document.documentElement.style.setProperty("overflow-y", "auto", "important");
    };

    const removeCookieHubElements = () => {
      document.querySelectorAll(cookieHubSelector).forEach((element) => {
        element.remove();
      });

      if ((window as any).cookiehub) {
        (window as any).cookiehub.hide?.();
        (window as any).cookiehub.close?.();
      }
    };

    enforceIframeStyles();

    const style = document.createElement("style");
    style.innerHTML = `
      html,
      body {
        overflow: auto !important;
      }

      #cookiehub-widget,
      .cookiehub-widget,
      [id*="cookiehub"],
      [class*="cookiehub"],
      [data-region][class*="ch2-region"],
      .ch2,
      .ch2-container,
      .ch2-dialog,
      .ch2-icon,
      style[data-ch2inline="true"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    removeCookieHubElements();

    const observer = new MutationObserver(() => {
      enforceIframeStyles();
      removeCookieHubElements();
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "class"],
    });

    return () => {
      observer.disconnect();
      restoreProperty(
        document.body.style,
        "overflow",
        previousBodyOverflow,
        previousBodyOverflowPriority
      );
      restoreProperty(
        document.body.style,
        "overflow-x",
        previousBodyOverflowX,
        previousBodyOverflowXPriority
      );
      restoreProperty(
        document.body.style,
        "overflow-y",
        previousBodyOverflowY,
        previousBodyOverflowYPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow",
        previousHtmlOverflow,
        previousHtmlOverflowPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow-x",
        previousHtmlOverflowX,
        previousHtmlOverflowXPriority
      );
      restoreProperty(
        document.documentElement.style,
        "overflow-y",
        previousHtmlOverflowY,
        previousHtmlOverflowYPriority
      );
      document.head.removeChild(style);
    };
  }, []);

  const {
    data: dataFiabilite,
    isFetching: isFetchingFiabilite,
    isError: isErrorFiabilite,
  } = useQuery({
    queryKey: [QUERY_KEYS.GET_DATA_FIABILITE, id],
    queryFn: () => API.mock.getDataFiabilite(id as string),
    retry: 0,
    enabled: isTokenSet && !!id,
  });

  const { data: dataDynamicInfo } = useQuery({
    queryKey: [QUERY_KEYS.GET_DYNAMIC_INFORMATION_RESULTAT, id],
    queryFn: () => API.mock.getDynamicInformationResultat(id as string),
    retry: 0,
    enabled: isTokenSet && !!id,
  });

  const {
    mutateAsync: mutateSVGFiabilite,
    isPending: isPendingSVGFiabilite,
  } = useMutation({
    mutationFn: (params: any) => API.mock.getSVGFiabilite(params),
  });

  useEffect(() => {
    if (!isTokenSet || !id) {
      return;
    }

    const loadSvgFiabilite = async () => {
      try {
        const result = await mutateSVGFiabilite({ ref_ademe: id });
        setSvgFiabilite(result);
      } catch (e: any) {
        messageApi.open({
          type: "error",
          content: "Something went wrong",
          duration: 2,
        });
      }
    };

    loadSvgFiabilite();
  }, [id, isTokenSet, messageApi, mutateSVGFiabilite]);

  const handleSetDataModalFiabilite = (data: any) => {
    setDataModalFiabilite(data);
    setIsShowModalFiabilite(true);
  };

  if (!isTokenSet || isPendingSVGFiabilite || (isFetchingFiabilite && _.isEmpty(dataFiabilite))) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin indicator={<LoadingOutlined />} size="large" />
      </div>
    );
  }

  if (isErrorFiabilite) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p>Unable to load data</p>
      </div>
    );
  }

  return (
    <div className="energy-home energy-home--result iframe-fiabilite p-4">
      {contextHolder}

      <Fiabilite
        isFetchingFiabilite={isFetchingFiabilite || isPendingSVGFiabilite}
        dataFiabilite={dataFiabilite}
        svgFiabilite={svgFiabilite}
        dataDynamicInfo={dataDynamicInfo}
        onSetDataModalFiabilite={handleSetDataModalFiabilite}
        isIframe
      />

      <Modal
        open={isShowModalFiabilite}
        centered
        title={_.get(dataModalFiabilite, "details.title", "")}
        onCancel={() => setIsShowModalFiabilite(false)}
        onOk={() => setIsShowModalFiabilite(false)}
        cancelButtonProps={{ className: "hidden" }}
        wrapClassName="!z-[9999]"
      >
        <div className="p-4">
          <ul style={{ listStyleType: "disc" }} className="flex flex-col gap-2">
            {_.get(dataModalFiabilite, "details.body", []).map(
              (item: string, index: number) => (
                <li key={index}>{item}</li>
              )
            )}
          </ul>
        </div>
      </Modal>

      <ModalDynamicInfo dataDynamicInfo={dataDynamicInfo} />
    </div>
  );
}

export default IframeFiabilite;